/**
 * Markdown Editor Component
 * 
 * A full-featured markdown editor with live preview,
 * syntax highlighting, and API integration.
 */

import { APIClient } from './api-client';
import { showLoading, showError, showSuccess } from './utils';

export interface EditorOptions {
  containerId: string;
  apiEndpoint?: string;
  autoSave?: boolean;
  autoSaveInterval?: number; // milliseconds
}

export interface MarkdownDocument {
  id?: string;
  title: string;
  content: string;
  slug?: string;
  description?: string;
  date?: string;
  tags?: string[];
}

export class MarkdownEditor {
  private container: HTMLElement;
  private editor: HTMLTextAreaElement;
  private preview: HTMLElement;
  private api: APIClient;
  private options: EditorOptions;
  private autoSaveTimer?: number;
  private currentDocument: MarkdownDocument | null = null;

  constructor(options: EditorOptions) {
    const container = document.getElementById(options.containerId);
    if (!container) {
      throw new Error(`Container with id "${options.containerId}" not found`);
    }

    this.container = container;
    this.options = options;
    this.api = new APIClient({
      baseURL: options.apiEndpoint || '',
    });

    this.editor = document.createElement('textarea');
    this.preview = document.createElement('div');

    this.init();
  }

  /**
   * Initialize the editor
   */
  private init(): void {
    this.render();
    this.attachEventListeners();
    this.loadFromLocalStorage();

    if (this.options.autoSave) {
      this.startAutoSave();
    }
  }

  /**
   * Render the editor UI
   */
  private render(): void {
    this.container.innerHTML = `
      <div class="markdown-editor">
        <!-- Toolbar -->
        <div class="editor-toolbar">
          <div class="toolbar-group">
            <button class="toolbar-btn" data-action="bold" title="Bold (Ctrl+B)">
              <strong>B</strong>
            </button>
            <button class="toolbar-btn" data-action="italic" title="Italic (Ctrl+I)">
              <em>I</em>
            </button>
            <button class="toolbar-btn" data-action="heading" title="Heading">
              H1
            </button>
            <button class="toolbar-btn" data-action="link" title="Link">
              🔗
            </button>
            <button class="toolbar-btn" data-action="code" title="Code Block">
              &lt;/&gt;
            </button>
            <button class="toolbar-btn" data-action="quote" title="Quote">
              "
            </button>
            <button class="toolbar-btn" data-action="list" title="List">
              ≡
            </button>
          </div>
          
          <div class="toolbar-group">
            <button class="toolbar-btn toolbar-btn--primary" data-action="preview-toggle">
              👁️ Preview
            </button>
          </div>
          
          <div class="toolbar-group">
            <button class="toolbar-btn" data-action="save-local" title="Save to Browser">
              💾 Save Local
            </button>
            <button class="toolbar-btn toolbar-btn--success" data-action="save-api" title="Save to Database">
              ☁️ Save to DB
            </button>
          </div>
        </div>

        <!-- Metadata Form -->
        <div class="editor-metadata">
          <input type="text" id="doc-title" class="metadata-input" placeholder="Document Title" />
          <input type="text" id="doc-slug" class="metadata-input" placeholder="URL Slug (optional)" />
          <input type="text" id="doc-description" class="metadata-input" placeholder="Description (optional)" />
          <input type="text" id="doc-tags" class="metadata-input" placeholder="Tags (comma-separated)" />
        </div>

        <!-- Editor Panes -->
        <div class="editor-panes">
          <div class="editor-pane">
            <textarea id="markdown-input" class="markdown-textarea" placeholder="Write your markdown here..."></textarea>
          </div>
          <div class="preview-pane" id="markdown-preview" style="display: none;">
            <div class="preview-content"></div>
          </div>
        </div>

        <!-- Status Bar -->
        <div class="editor-status">
          <span id="editor-status-text">Ready</span>
          <span id="editor-word-count">0 words</span>
        </div>
      </div>
    `;

    this.editor = this.container.querySelector('#markdown-input') as HTMLTextAreaElement;
    this.preview = this.container.querySelector('.preview-content') as HTMLElement;
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    // Toolbar actions
    this.container.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const action = (e.currentTarget as HTMLElement).dataset.action;
        if (action) {
          this.handleToolbarAction(action);
        }
      });
    });

    // Editor input
    this.editor.addEventListener('input', () => {
      this.updatePreview();
      this.updateWordCount();
      this.updateStatus('Editing...');
    });

    // Keyboard shortcuts
    this.editor.addEventListener('keydown', (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'b':
            e.preventDefault();
            this.insertFormatting('**', '**');
            break;
          case 'i':
            e.preventDefault();
            this.insertFormatting('_', '_');
            break;
          case 's':
            e.preventDefault();
            this.saveToLocalStorage();
            break;
        }
      }
    });

    // Auto-resize textarea
    this.editor.addEventListener('input', () => {
      this.editor.style.height = 'auto';
      this.editor.style.height = this.editor.scrollHeight + 'px';
    });
  }

  /**
   * Handle toolbar actions
   */
  private handleToolbarAction(action: string): void {
    switch (action) {
      case 'bold':
        this.insertFormatting('**', '**');
        break;
      case 'italic':
        this.insertFormatting('_', '_');
        break;
      case 'heading':
        this.insertAtLineStart('## ');
        break;
      case 'link':
        this.insertFormatting('[', '](url)');
        break;
      case 'code':
        this.insertFormatting('```\n', '\n```');
        break;
      case 'quote':
        this.insertAtLineStart('> ');
        break;
      case 'list':
        this.insertAtLineStart('- ');
        break;
      case 'preview-toggle':
        this.togglePreview();
        break;
      case 'save-local':
        this.saveToLocalStorage();
        break;
      case 'save-api':
        this.saveToAPI();
        break;
    }
  }

  /**
   * Insert formatting around selection
   */
  private insertFormatting(before: string, after: string): void {
    const start = this.editor.selectionStart;
    const end = this.editor.selectionEnd;
    const text = this.editor.value;
    const selection = text.substring(start, end);

    const newText = text.substring(0, start) + before + selection + after + text.substring(end);
    this.editor.value = newText;
    this.editor.selectionStart = start + before.length;
    this.editor.selectionEnd = end + before.length;
    this.editor.focus();

    this.updatePreview();
  }

  /**
   * Insert text at the start of the current line
   */
  private insertAtLineStart(text: string): void {
    const start = this.editor.selectionStart;
    const value = this.editor.value;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;

    const newText = value.substring(0, lineStart) + text + value.substring(lineStart);
    this.editor.value = newText;
    this.editor.selectionStart = this.editor.selectionEnd = start + text.length;
    this.editor.focus();

    this.updatePreview();
  }

  /**
   * Toggle preview pane
   */
  private togglePreview(): void {
    const previewPane = this.container.querySelector('.preview-pane') as HTMLElement;
    const editorPane = this.container.querySelector('.editor-pane') as HTMLElement;

    if (previewPane.style.display === 'none') {
      previewPane.style.display = 'block';
      editorPane.style.width = '50%';
      this.updatePreview();
    } else {
      previewPane.style.display = 'none';
      editorPane.style.width = '100%';
    }
  }

  /**
   * Update markdown preview
   */
  private updatePreview(): void {
    // Basic markdown to HTML conversion
    // In production, use a library like marked.js or markdown-it
    let html = this.editor.value;

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Italic
    html = html.replace(/\_(.*?)\_/g, '<em>$1</em>');

    // Links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>');

    // Code blocks
    html = html.replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>');

    // Inline code
    html = html.replace(/`(.*?)`/g, '<code>$1</code>');

    // Line breaks
    html = html.replace(/\n/g, '<br>');

    this.preview.innerHTML = html;
  }

  /**
   * Update word count
   */
  private updateWordCount(): void {
    const words = this.editor.value.trim().split(/\s+/).length;
    const wordCountEl = this.container.querySelector('#editor-word-count');
    if (wordCountEl) {
      wordCountEl.textContent = `${words} words`;
    }
  }

  /**
   * Update status message
   */
  private updateStatus(message: string): void {
    const statusEl = this.container.querySelector('#editor-status-text');
    if (statusEl) {
      statusEl.textContent = message;
    }
  }

  /**
   * Get current document data
   */
  private getDocumentData(): MarkdownDocument {
    const titleInput = this.container.querySelector('#doc-title') as HTMLInputElement;
    const slugInput = this.container.querySelector('#doc-slug') as HTMLInputElement;
    const descInput = this.container.querySelector('#doc-description') as HTMLInputElement;
    const tagsInput = this.container.querySelector('#doc-tags') as HTMLInputElement;

    return {
      title: titleInput?.value || 'Untitled',
      content: this.editor.value,
      slug: slugInput?.value || undefined,
      description: descInput?.value || undefined,
      tags: tagsInput?.value ? tagsInput.value.split(',').map((t) => t.trim()) : undefined,
      date: new Date().toISOString(),
    };
  }

  /**
   * Save to localStorage
   */
  public saveToLocalStorage(): void {
    try {
      const doc = this.getDocumentData();
      localStorage.setItem('markdown-editor-content', JSON.stringify(doc));
      this.updateStatus('✅ Saved locally');
      setTimeout(() => this.updateStatus('Ready'), 2000);
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
      this.updateStatus('❌ Failed to save locally');
    }
  }

  /**
   * Load from localStorage
   */
  private loadFromLocalStorage(): void {
    try {
      const saved = localStorage.getItem('markdown-editor-content');
      if (saved) {
        const doc: MarkdownDocument = JSON.parse(saved);
        this.loadDocument(doc);
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
    }
  }

  /**
   * Save to API
   */
  public async saveToAPI(): Promise<void> {
    if (!this.options.apiEndpoint) {
      alert('No API endpoint configured. Please set apiEndpoint in options.');
      return;
    }

    try {
      this.updateStatus('Saving to database...');
      const doc = this.getDocumentData();

      const response = await this.api.post<{ id: string; message: string }>(
        '/documents',
        doc
      );

      this.currentDocument = { ...doc, id: response.id };
      this.updateStatus(`✅ Saved to database (ID: ${response.id})`);
      setTimeout(() => this.updateStatus('Ready'), 3000);
    } catch (error) {
      console.error('Failed to save to API:', error);
      this.updateStatus('❌ Failed to save to database');
      alert('Failed to save to database. Check console for details.');
    }
  }

  /**
   * Load a document
   */
  public loadDocument(doc: MarkdownDocument): void {
    this.currentDocument = doc;
    this.editor.value = doc.content;

    const titleInput = this.container.querySelector('#doc-title') as HTMLInputElement;
    const slugInput = this.container.querySelector('#doc-slug') as HTMLInputElement;
    const descInput = this.container.querySelector('#doc-description') as HTMLInputElement;
    const tagsInput = this.container.querySelector('#doc-tags') as HTMLInputElement;

    if (titleInput) titleInput.value = doc.title;
    if (slugInput && doc.slug) slugInput.value = doc.slug;
    if (descInput && doc.description) descInput.value = doc.description;
    if (tagsInput && doc.tags) tagsInput.value = doc.tags.join(', ');

    this.updatePreview();
    this.updateWordCount();
    this.updateStatus('Document loaded');
  }

  /**
   * Start auto-save
   */
  private startAutoSave(): void {
    const interval = this.options.autoSaveInterval || 30000; // 30 seconds default

    this.autoSaveTimer = window.setInterval(() => {
      if (this.editor.value.trim()) {
        this.saveToLocalStorage();
      }
    }, interval);
  }

  /**
   * Stop auto-save
   */
  public stopAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }
  }

  /**
   * Get current content
   */
  public getContent(): string {
    return this.editor.value;
  }

  /**
   * Set content
   */
  public setContent(content: string): void {
    this.editor.value = content;
    this.updatePreview();
    this.updateWordCount();
  }

  /**
   * Clear editor
   */
  public clear(): void {
    this.editor.value = '';
    this.currentDocument = null;

    const inputs = this.container.querySelectorAll('.metadata-input') as NodeListOf<HTMLInputElement>;
    inputs.forEach((input) => (input.value = ''));

    this.updatePreview();
    this.updateWordCount();
    this.updateStatus('Editor cleared');
  }

  /**
   * Destroy editor and cleanup
   */
  public destroy(): void {
    this.stopAutoSave();
    this.container.innerHTML = '';
  }
}

